// src/components/ParentStudentDetail.js - Sửa lỗi phân quyền & JSON Parse
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchAcademicYearsListRest,
  fetchParentStudentDetailRest
} from '../FirebaseService';
import './ParentStudentDetail.css';

const PAGE_SIZE = 10;

// Hàm Parse JSON an toàn chống lỗi "undefined is not valid JSON"
const safeJsonParse = (str, fallback = null) => {
  if (!str || str === 'undefined' || str === 'null') return fallback;
  try {
    return JSON.parse(str);
  } catch (e) {
    return fallback;
  }
};

const ParentStudentDetail = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  // State Niên khóa
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');

  // State Dữ liệu tổng hợp
  const [studentDetail, setStudentDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Tabs & Filters
  const [activeTab, setActiveTab] = useState('attendance');
  const [attSubTab, setAttSubTab] = useState('church');
  const [visibleChurchCount, setVisibleChurchCount] = useState(PAGE_SIZE);
  const [visibleCatechismCount, setVisibleCatechismCount] = useState(PAGE_SIZE);

  const [dayFilters, setDayFilters] = useState({
    'Chúa nhật': false, 'Thứ hai': false, 'Thứ ba': false, 'Thứ tư': false,
    'Thứ năm': false, 'Thứ sáu': false, 'Thứ bảy': false
  });

  // 1. TẢI DANH SÁCH NIÊN KHÓA QUA REST API THUẦN (Zero Auth Permission Error)
  useEffect(() => {
    let isMounted = true;
    fetchAcademicYearsListRest()
      .then((years) => {
        if (!isMounted) return;
        if (Array.isArray(years) && years.length > 0) {
          setAcademicYears(years);
          const current = years.find((y) => y.isCurrent) || years[0];
          if (current) setSelectedYearId(current.id);
        } else {
          setErrorMessage('Không thể tải danh sách niên khóa.');
        }
      })
      .catch((err) => {
        console.error('Lỗi khi tải niên khóa REST:', err);
        if (isMounted) setErrorMessage('Lỗi kết nối máy chủ.');
      });

    return () => { isMounted = false; };
  }, []);

  // 2. TẢI CHI TIẾT HỒ SƠ, LỚP HỌC, BẢNG ĐIỂM & ĐIỂM DANH SANG SONG QUA REST API
  useEffect(() => {
    if (!studentId || !selectedYearId) return;

    let isMounted = true;
    setIsLoading(true);
    setErrorMessage('');

    fetchParentStudentDetailRest(studentId, selectedYearId)
      .then((res) => {
        if (!isMounted) return;
        if (res.error) {
          setErrorMessage(res.error);
        } else {
          setStudentDetail(res);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Lỗi khi tải chi tiết thiếu nhi REST:', err);
        if (isMounted) {
          setErrorMessage('Lỗi hệ thống khi tải thông tin thiếu nhi.');
          setIsLoading(false);
        }
      });

    return () => { isMounted = false; };
  }, [studentId, selectedYearId]);

  const handleDayFilterChange = (day) => {
    setDayFilters((prev) => ({ ...prev, [day]: !prev[day] }));
    setVisibleChurchCount(PAGE_SIZE);
    setVisibleCatechismCount(PAGE_SIZE);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    const daysOfWeek = ['Chúa nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
    return `${daysOfWeek[dateObj.getDay()]} - ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  };

  const formatDob = (dobStr) => {
    if (!dobStr) return 'Chưa cập nhật';
    const [y, m, d] = dobStr.split('-');
    return (y && m && d) ? `${d}/${m}/${y}` : dobStr;
  };

  const sortedChurchAttendance = useMemo(() => {
    if (!studentDetail?.churchAttendance) return [];
    return Object.entries(studentDetail.churchAttendance).sort(([a], [b]) => b.localeCompare(a));
  }, [studentDetail]);

  const sortedCatechismAttendance = useMemo(() => {
    if (!studentDetail?.catechismAttendance) return [];
    return Object.entries(studentDetail.catechismAttendance).sort(([a], [b]) => b.localeCompare(a));
  }, [studentDetail]);

  const filterAttendanceByDay = (attendanceArr) => {
    const hasActiveFilter = Object.values(dayFilters).some((v) => v);
    if (!hasActiveFilter) return attendanceArr;

    return attendanceArr.filter(([dateStr]) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const dayName = ['Chúa nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'][new Date(year, month - 1, day).getDay()];
      return dayFilters[dayName];
    });
  };

  const filteredChurch = useMemo(() => filterAttendanceByDay(sortedChurchAttendance), [sortedChurchAttendance, dayFilters]);
  const filteredCatechism = useMemo(() => filterAttendanceByDay(sortedCatechismAttendance), [sortedCatechismAttendance, dayFilters]);

  const getStatusBadge = (statusObj) => {
    const str = String(typeof statusObj === 'object' ? statusObj?.time : statusObj || 'Chưa điểm danh').trim();
    if (str === 'Chưa điểm danh' || str === 'Vắng không xin phép') return { class: 'status-absent', text: 'Vắng mặt' };
    if (str === 'Vắng có xin phép') return { class: 'status-permitted', text: 'Có xin phép' };
    return { class: 'status-present', text: str };
  };

  return (
    <div className="psd-wrapper">
      <header className="psd-header">
        <button onClick={() => navigate('/parent')} className="psd-back-btn">← Quay lại tra cứu</button>
        <span className="psd-header-title">Thông Tin Thiếu Nhi</span>
      </header>

      <div className="psd-container">
        {errorMessage ? (
          <div className="psd-error-card">
            <p>❌ {errorMessage}</p>
            <button onClick={() => navigate('/parent')} className="psd-retry-btn">Quay lại</button>
          </div>
        ) : (
          <>
            {/* 🌟 HỒ SƠ CÁ NHÂN */}
            <div className="psd-profile-card">
              {isLoading && !studentDetail ? (
                <div className="psd-loading-card">
                  <div className="psd-spinner"></div>
                  <p>⏳ Đang tải thông tin thiếu nhi...</p>
                </div>
              ) : studentDetail?.profile && (
                <>
                  <div className="psd-profile-top">
                    <div className="psd-avatar-badge">{studentDetail.profile.gender === 'nam' ? '👦' : '👧'}</div>
                    <div className="psd-profile-titles">
                      <span className="psd-holy-name">{studentDetail.profile.holyName || 'Tên Thánh'}</span>
                      <h2 className="psd-full-name">{studentDetail.profile.fullName}</h2>
                    </div>
                  </div>

                  <div className="psd-info-grid">
                    <div className="psd-info-item"><span className="info-label">Mã ID</span><span className="info-value id-highlight">{studentId}</span></div>
                    <div className="psd-info-item"><span className="info-label">Lớp học</span><span className="info-value">{studentDetail.className}</span></div>
                    <div className="psd-info-item"><span className="info-label">Giới tính</span><span className="info-value">{studentDetail.profile.gender === 'nam' ? 'Nam 👦' : 'Nữ 👧'}</span></div>
                    <div className="psd-info-item"><span className="info-label">Ngày sinh</span><span className="info-value">{formatDob(studentDetail.profile.dateOfBirth)}</span></div>
                    <div className="psd-info-item span-full">
                      <span className="info-label">Trạng thái</span>
                      <span className={`psd-status-pill ${studentDetail.profile.status === 'Nghỉ luôn' ? 'status-off' : 'status-on'}`}>
                        ● {studentDetail.profile.status || 'Còn đi học'}
                      </span>
                    </div>
                  </div>

                  <div className="psd-year-selector-box">
                    <label>Niên khóa xem dữ liệu:</label>
                    <select value={selectedYearId} onChange={(e) => setSelectedYearId(e.target.value)} className="psd-year-select">
                      {academicYears.map((y) => (
                        <option key={y.id} value={y.id}>{y.name} {y.isCurrent ? '(Hiện tại)' : ''}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* TAB CHÍNH */}
            <div className="psd-tabs">
              <button className={`psd-tab-btn ${activeTab === 'attendance' ? 'active' : ''}`} onClick={() => setActiveTab('attendance')}>⛪ Điểm Danh</button>
              <button className={`psd-tab-btn ${activeTab === 'transcript' ? 'active' : ''}`} onClick={() => setActiveTab('transcript')}>📊 Bảng Điểm</button>
              <button className={`psd-tab-btn ${activeTab === 'qrcode' ? 'active' : ''}`} onClick={() => setActiveTab('qrcode')}>🪪 Thẻ QR</button>
            </div>

            {/* TAB ĐIỂM DANH */}
            {activeTab === 'attendance' && (
              <div className="psd-tab-content">
                <div className="psd-sub-tabs">
                  <button className={`psd-subtab-btn ${attSubTab === 'church' ? 'active' : ''}`} onClick={() => setAttSubTab('church')}>⛪ Đi Lễ Chúa Nhật</button>
                  <button className={`psd-subtab-btn catechism ${attSubTab === 'catechism' ? 'active' : ''}`} onClick={() => setAttSubTab('catechism')}>📖 Học Giáo Lý</button>
                </div>

                <div className="psd-filter-row">
                  <span className="filter-label">Lọc theo ngày trong tuần:</span>
                  <div className="filter-chips">
                    {Object.keys(dayFilters).map((day) => (
                      <button key={day} className={`filter-chip ${dayFilters[day] ? 'selected' : ''}`} onClick={() => handleDayFilterChange(day)}>{day}</button>
                    ))}
                  </div>
                </div>

                {isLoading ? (
                  <div className="psd-loading-card">
                    <div className="psd-spinner"></div>
                    <p>⏳ Đang tải nhật ký điểm danh...</p>
                  </div>
                ) : (
                  <>
                    {attSubTab === 'church' && (
                      <div className="psd-section-box">
                        <h3>⛪ Lịch Sử Đi Lễ Chúa Nhật</h3>
                        <div className="psd-att-list">
                          {filteredChurch.length > 0 ? (
                            <>
                              {filteredChurch.slice(0, visibleChurchCount).map(([date, status]) => {
                                const badge = getStatusBadge(status);
                                return (
                                  <div key={date} className="psd-att-item">
                                    <span className="att-date">{formatDisplayDate(date)}</span>
                                    <span className={`att-status ${badge.class}`}>{badge.text}</span>
                                  </div>
                                );
                              })}
                              {visibleChurchCount < filteredChurch.length && (
                                <button className="psd-load-more-btn" onClick={() => setVisibleChurchCount((prev) => prev + PAGE_SIZE)}>
                                  🔽 Xem thêm ngày cũ hơn ({filteredChurch.length - visibleChurchCount})
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="psd-empty-text">Chưa có nhật ký đi lễ trong các ngày đã chọn.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {attSubTab === 'catechism' && (
                      <div className="psd-section-box">
                        <h3>📖 Lịch Sử Học Giáo Lý</h3>
                        <div className="psd-att-list">
                          {filteredCatechism.length > 0 ? (
                            <>
                              {filteredCatechism.slice(0, visibleCatechismCount).map(([date, status]) => {
                                const badge = getStatusBadge(status);
                                return (
                                  <div key={date} className="psd-att-item">
                                    <span className="att-date">{formatDisplayDate(date)}</span>
                                    <span className={`att-status ${badge.class}`}>{badge.text}</span>
                                  </div>
                                );
                              })}
                              {visibleCatechismCount < filteredCatechism.length && (
                                <button className="psd-load-more-btn" onClick={() => setVisibleCatechismCount((prev) => prev + PAGE_SIZE)}>
                                  🔽 Xem thêm ngày cũ hơn ({filteredCatechism.length - visibleCatechismCount})
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="psd-empty-text">Chưa có nhật ký học giáo lý trong các ngày đã chọn.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* TAB BẢNG ĐIỂM */}
            {activeTab === 'transcript' && (
              <div className="psd-tab-content">
                {isLoading ? (
                  <div className="psd-loading-card">
                    <div className="psd-spinner"></div>
                    <p>⏳ Đang tải bảng điểm...</p>
                  </div>
                ) : studentDetail?.transcript ? (
                  <div className="psd-scores-grid">
                    <div className="psd-score-card">
                      <h4>📘 Học Kỳ I</h4>
                      <div className="score-rows">
                        <div className="score-row"><span>Miệng:</span> <strong>{studentDetail.transcript.semester1?.oral ?? '-'}</strong></div>
                        <div className="score-row"><span>15 phút:</span> <strong>{studentDetail.transcript.semester1?.fifteenMinutes ?? '-'}</strong></div>
                        <div className="score-row"><span>1 tiết:</span> <strong>{studentDetail.transcript.semester1?.onePeriod ?? '-'}</strong></div>
                        <div className="score-row"><span>Thi:</span> <strong>{studentDetail.transcript.semester1?.exam ?? '-'}</strong></div>
                        <div className="score-row highlight"><span>Trung bình HK1:</span> <strong>{studentDetail.transcript.semester1?.average ?? '-'}</strong></div>
                      </div>
                    </div>

                    <div className="psd-score-card">
                      <h4>📗 Học Kỳ II</h4>
                      <div className="score-rows">
                        <div className="score-row"><span>Miệng:</span> <strong>{studentDetail.transcript.semester2?.oral ?? '-'}</strong></div>
                        <div className="score-row"><span>15 phút:</span> <strong>{studentDetail.transcript.semester2?.fifteenMinutes ?? '-'}</strong></div>
                        <div className="score-row"><span>1 tiết:</span> <strong>{studentDetail.transcript.semester2?.onePeriod ?? '-'}</strong></div>
                        <div className="score-row"><span>Thi:</span> <strong>{studentDetail.transcript.semester2?.exam ?? '-'}</strong></div>
                        <div className="score-row highlight"><span>Trung bình HK2:</span> <strong>{studentDetail.transcript.semester2?.average ?? '-'}</strong></div>
                      </div>
                    </div>

                    <div className="psd-score-card yearly">
                      <h4>🏆 Tổng Kết Cả Năm</h4>
                      <div className="score-rows">
                        <div className="score-row highlight-final"><span>Trung Bình Cả Năm:</span> <strong>{studentDetail.transcript.yearly?.average ?? '-'}</strong></div>
                        <div className="score-row"><span>Thi vấn đáp:</span> <strong>{studentDetail.transcript.yearly?.oralExam ?? '-'}</strong></div>
                        <div className="score-row"><span>Chuyên cần:</span> <strong>{studentDetail.transcript.yearly?.diligence ?? '-'}</strong></div>
                        <div className="score-row"><span>Xếp hạng:</span> <strong>{studentDetail.transcript.yearly?.rank ?? '-'}</strong></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="psd-empty-box"><p>Chưa có dữ liệu bảng điểm cho niên khóa này.</p></div>
                )}
              </div>
            )}

            {/* TAB THẺ QR */}
            {activeTab === 'qrcode' && (
              <div className="psd-tab-content center-tab">
                <div className="psd-qr-card">
                  <h3>Thẻ Điểm Danh Điện Tử</h3>
                  <p>Phụ huynh đưa mã QR này cho Giáo lý viên quét khi con đi lễ / đi học</p>
                  {studentId ? (
                    <div className="psd-qr-box">
                      <QRCodeSVG
                        value={studentId}
                        size={180}
                        level="M"
                        className="psd-qr-image"
                      />
                    </div>
                  ) : (
                    <p className="psd-empty-text">Không có mã QR</p>
                  )}
                  <span className="psd-qr-student-id">{studentId}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ParentStudentDetail;